<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.segmentId" :icon="faScreenUsers">
            {{ props.templateView ? 'New Segment Template' : 'New Segment' }}

            <template v-if="props.templateView" v-slot:description>
                Create a segment template that can be shared with groups.
            </template>
            <template v-else v-slot:description> Create automated groups of contacts. </template>
        </step-form-heading>

        <page-header v-if="props.segmentId" class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a
                    :href="props.templateView ? '/contacts/segments/templates' : '/contacts/segments'"
                    class="no-underline! text-sky-600! text-base font-normal"
                >
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All
                    {{ props.templateView ? 'Segment Templates' : 'Segments' }}
                </a>
            </template>

            {{ segment.name }}
        </page-header>

        <div class="grid grid-cols-12">
            <page-sidebar
                v-if="props.segmentId"
                :links="
                    [
                        {
                            href: `/contacts/segments/${props.templateView ? 'templates/' : ''}${props.segmentId}`,
                            label: 'Details',
                        },
                        {
                            href: `/contacts/segments/${props.templateView ? 'templates/' : ''}${
                                props.segmentId
                            }#filters`,
                            label: 'Filters',
                        },
                        !props.templateView && onCorporate()
                            ? {
                                  href: `/contacts/segments/${props.segmentId}/contacts`,
                                  label: 'Contacts',
                              }
                            : null,
                    ].filter((link) => Boolean(link))
                "
            ></page-sidebar>
            <form-wrapper
                :sections="
                    [
                        { id: 'name', label: 'Name' },
                        { id: 'description', label: 'Description' },
                        { id: 'category', label: 'Category' },
                        onCorporate() && !props.templateView ? { id: 'type', label: 'Type' } : null,
                        { id: 'audience', label: 'Audience' },
                        showGroupsSection ? { id: 'group', label: 'Groups' } : null,
                        { id: 'filters', label: 'Filters' },
                    ].filter((s) => !!s)
                "
                :hideNav="Boolean(props.segmentId)"
            >
                <div v-if="segment?.flow_id">
                    <flow-resource-state-alert
                        ref="flowAlertRef"
                        :resourceName="segment?.name"
                        :editing="!!segment?.id"
                        :flowId="segment?.flow_id"
                        :flowSection="segment?.flow_section"
                    >
                    </flow-resource-state-alert>
                </div>

                <segment-step-form-name
                    id="name"
                    v-model="segment.name"
                    :error="displayFirstError(validation.name.$errors)"
                ></segment-step-form-name>

                <segment-step-form-description
                    id="description"
                    v-model="segment.description"
                    :error="displayFirstError(validation.description)"
                ></segment-step-form-description>

                <resource-category
                    id="category"
                    v-model="segment.category"
                    :disabled="!!segment.flow_id"
                    @reset="segment.category = null"
                >
                </resource-category>

                <segment-step-form-type
                    v-if="onCorporate() && !props.templateView"
                    id="type"
                    v-model="segment.type"
                    :disabled="!!props.segmentId"
                ></segment-step-form-type>

                <segment-step-form-audience
                    id="audience"
                    v-model="segment.audience_type"
                    :templateView="props.templateView"
                    :disabled="!!props.segmentId"
                ></segment-step-form-audience>

                <segment-step-form-group-preferred
                    v-if="FEATURES.contact_groups && props.groupId"
                    id="group"
                    v-model="segment.preferred_group"
                ></segment-step-form-group-preferred>

                <segment-step-form-group-collection
                    v-if="
                        FEATURES.contact_groups &&
                        !props.templateView &&
                        onCorporate() &&
                        segment.type !== SegmentType.STATIC
                    "
                    id="group"
                    v-model="segment.group_collection_id"
                ></segment-step-form-group-collection>

                <segment-step-form-group-access
                    v-if="FEATURES.contact_groups && props.templateView"
                    id="group"
                    :segmentId="props.segmentId"
                    v-model:groupFilters="segment.group_filters"
                    v-model:groupCollectionId="segment.group_collection_id"
                    v-model:preferredGroup="segment.preferred_group"
                    :error="displayFirstError(validation.group_filters.$errors)"
                ></segment-step-form-group-access>

                <segment-step-form-filters
                    v-if="!props.segmentId || (props.segmentId && segment.id)"
                    id="filters"
                    v-model="segment.filter_groups"
                    :error="
                        displayFirstError(validation.filter_groups.$errors) ||
                        displayFirstError(validation.total_filters.$errors)
                    "
                ></segment-step-form-filters>

                <vx-sticky-footer>
                    <vx-button
                        :href="props.templateView ? '/contacts/segments/templates' : '/contacts/segments'"
                        data-test="cancel-button"
                        class="w-20"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="submit-btn"
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="processing"
                    >
                        {{ props.segmentId ? 'Update' : 'Create' }}
                        {{ props.templateView ? 'Template' : 'Segment' }}
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>
        </div>
    </form-container>
</template>

<script setup>
import { VxStickyFooter, VxButton } from '@voxie/frontend-components';
import StepFormHeading from '../../../general/StepFormHeading.vue';
import SegmentStepFormName from './SegmentStepFormName.vue';
import SegmentStepFormType from './SegmentStepFormType.vue';
import SegmentStepFormAudience from './SegmentStepFormAudience.vue';
import SegmentStepFormGroupPreferred from './SegmentStepFormGroupPreferred.vue';
import SegmentStepFormGroupAccess from './SegmentStepFormGroupAccess.vue';
import SegmentStepFormGroupCollection from './SegmentStepFormGroupCollection.vue';
import SegmentStepFormFilters from './segment-step-form-filters/SegmentStepFormFilters.vue';
import PageHeader from '../../../general/PageHeader.vue';
import PageLoading from '../../../general/PageLoading.vue';
import PageSidebar from '../../../general/PageSidebar.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft, faScreenUsers, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { ref, inject, onMounted, computed, watch } from 'vue';
import { segmentsClient, flowsClient } from '~/services';
import { SegmentType } from '../../../../constants/segment';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '../../../../constants/campaign';
import { useUrlSearchParams } from '@vueuse/core';
import FlowResourceStateAlert from '~/components/automation/flows/components/FlowResourceStateAlert.vue';
import ResourceCategory from '~/components/general/categories/ResourceCategory.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { displayFirstError } from '~/utils/validation';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { helpers, maxLength, maxValue, required } from '@vuelidate/validators';
import FormWrapper from '~/components/general/FormWrapper.vue';
import SegmentStepFormDescription from '~/components/contacts/segments/segment-step-form/SegmentStepFormDescription.vue';

const FEATURES = inject('FEATURES');

const props = defineProps({
    segmentId: {
        type: Number,
        required: false,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();
const loading = ref(false);
const segment = ref({
    name: '',
    description: null,
    type: SegmentType.AUTO_REFRESH,
    audience_type: FEATURES.transactional_default ? AUDIENCE_TYPE_TRANSACTIONAL : AUDIENCE_TYPE_MARKETING,
    preferred_group: false,
    group_collection_id: null,
    filter_groups: [],
    group_filters: [],
    category: null,
});

const flowAlertRef = ref();
const savedSegmentId = ref();

const validationRules = computed(() => ({
    name: {
        required,
        maxLength: maxLength(96),
        regex: helpers.withMessage(
            'Segment name is invalid',
            helpers.regex(/^[a-zA-Z0-9][a-zA-Z0-9:/\\@#$%&*()\-=_+[\],?. ]*$/)
        ),
    },
    description: {
        maxLength: maxLength(2000),
    },
    audience_type: {
        required,
    },
    type: {
        required,
    },
    filter_groups: {
        maxLength: helpers.withMessage('Segment should not contain more than 10 Filter Groups.', maxLength(10)),
    },
    total_filters: {
        maxValue: helpers.withMessage('Segment should not contain more than 100 Filters.', maxValue(100)),
    },
    group_filters: {
        maxLength: helpers.withMessage('Segment should not contain more than 10 Group Filters.', maxLength(10)),
    },
}));

const totalFiltersCount = computed(() =>
    segment.value.filter_groups.reduce((total, group) => {
        return total + group.filters.length;
    }, 0)
);

const context = computed(() => ({ teamId, orgUnitId: props.orgUnitId, groupId: props.groupId }));

const saveSegment = async () => {
    let data;

    if (onCorporate()) {
        data = segment.value;
    } else if (props.orgUnitId || props.groupId) {
        data = {
            name: segment.value.name,
            audience_type: segment.value.audience_type,
            category: segment.value.category,
            filter_groups: segment.value.filter_groups,
        };

        if (!props.orgUnitId && props.groupId) {
            data.preferred_group = segment.value.preferred_group;
        }
    }

    data.description = segment.value.description || null;

    const response = props.segmentId
        ? await segmentsClient.updateSegment(context.value, props.segmentId, data)
        : await segmentsClient.createSegment(context.value, data);

    savedSegmentId.value = response.data.id;
};

const resetForm = () => {
    segment.value = {
        name: '',
        type: SegmentType.AUTO_REFRESH,
        audience_type: FEATURES.transactional_default ? AUDIENCE_TYPE_TRANSACTIONAL : AUDIENCE_TYPE_MARKETING,
        preferred_group: false,
        group_collection_id: null,
        filter_groups: [],
        group_filters: [],
        category: null,
    };
};

const queryParams = useUrlSearchParams();

const duplicate = async () => {
    const response = await segmentsClient.getSegment(context.value, queryParams.duplicateFromId);

    let flowSection;
    if (response.data.flow_id) {
        try {
            const {
                data: {
                    config: { sections },
                },
            } = await flowsClient.getConfig(teamId, response.data.flow_id);
            flowSection = sections.findIndex((section) =>
                section.resources.find(
                    (resource) =>
                        resource.type === 'segment' && Number(resource.id) === Number(queryParams.duplicateFromId)
                )
            );
        } catch (e) {
            console.error(e);
        }
    }

    segment.value = {
        ...response.data,
        id: undefined,
        name: '',
        flow_section: flowSection === -1 ? 0 : flowSection,
    };
};

onMounted(async () => {
    loading.value = true;
    try {
        if (props.segmentId) {
            segment.value = (await segmentsClient.getSegment(context.value, props.segmentId)).data;
        }

        if (queryParams.duplicateFromId) {
            await duplicate();
        }

        if (!segment.value.flow_id && queryParams.flow_id) {
            segment.value.flow_id = queryParams.flow_id;
            segment.value.flow_section = queryParams.flow_section || 0;
        }

        segment.value.is_template = props.templateView;
    } catch (e) {
        console.error(e);
    } finally {
        loading.value = false;
    }
});

const showGroupsSection = computed(() => {
    return (
        FEATURES.contact_groups &&
        (props.templateView ||
            (!props.orgUnitId && props.groupId) ||
            (!props.templateView && onCorporate() && segment.value.type !== SegmentType.STATIC))
    );
});

watch(
    () => segment.value.type,
    (type) => {
        if (type === SegmentType.STATIC) {
            segment.value.group_collection_id = null;
        }
    }
);

const { submit, processing, validation } = useFormSubmission({
    data: computed(() => ({
        ...segment.value,
        total_filters: totalFiltersCount.value,
    })),
    validation: validationRules,
    preserveScroll: () => Boolean(props.segmentId),
    submit: saveSegment,
    reset: () => {
        if (!props.segmentId && !segment.value.flow_id) {
            resetForm();
        }
    },
    notification: () =>
        segment.value.flow_id
            ? props.segmentId
                ? {
                      message: 'Segment updated',
                      action: {
                          icon: faSquareArrowUpLeft,
                          label: 'Go to Flow',
                          href: flowAlertRef.value.flowLink,
                      },
                  }
                : {
                      message: 'Segment created',
                      redirect: flowAlertRef.value.flowLink,
                      action: {
                          icon: faSquareArrowUpLeft,
                          label: 'Edit Segment',
                          href: `/contacts/segments/${savedSegmentId.value}`,
                      },
                  }
            : {
                  message: `${props.templateView ? 'Template' : 'Segment'} ${props.segmentId ? 'updated' : 'created'}`,
                  action: {
                      icon: faSquareArrowUpLeft,
                      label: `Back to ${props.templateView ? 'Templates' : 'Segments'}`,
                      href: props.templateView ? '/contacts/segments/templates' : '/contacts/segments',
                  },
              },
});
</script>
